import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { OCPP_1_6_MESSAGES } from "../../../lib/components/Filterbar/constants";
import { startCharging, stopCharging } from "../hooks/api";
import { fetchHierarchyCached } from "../../../lib/hooks/useHierarchy";
import { isEnphaseUser } from "../../../lib/utils/utility";

/**
 * Validate breaker capacity before starting a charger.
 *
 * Hierarchy from API: stations[] → station.sites[] → site.chargers[]
 * Each station has maxBreakerCurrent. Each charger has maxCurrent & status.
 * If the site is not assigned to any station, skip the check.
 */
async function validateBreakerCapacity(queryClient, siteId, chargerSerial) {
  // Enphase users don't have station data — skip breaker validation
  if (isEnphaseUser()) return { skipCheck: true };

  const stations = await fetchHierarchyCached(queryClient);

  // Find the station that contains this site
  let parentStation = null;

  for (const station of stations) {
    const sites = station.sites || [];
    for (const site of sites) {
      if (String(site.siteId) === String(siteId)) {
        parentStation = station;
        break;
      }
    }
    if (parentStation) break;
  }

  // If site is not assigned to any station, skip the check
  if (!parentStation) {
    return { skipCheck: true };
  }

  const maxBreakerCurrent = parentStation.maxBreakerCurrent;
  const stationName = parentStation.stationName || parentStation.name || "Unknown Station";

  // Find the target charger's maxCurrent from the hierarchy
  const allChargers = (parentStation.sites || []).flatMap(site => site.chargers || []);
  const targetCharger = allChargers.find(c => String(c.serialNumber || c.serial) === String(chargerSerial));
  const chargerMaxCurrent = targetCharger?.maxCurrent || targetCharger?.ratedCurrent || 0;

  // Sum maxCurrent of all currently-charging chargers in the station (across all sites)
  const currentLoad = allChargers
    .filter(c => c.status === "charging" || c.status === "Charging")
    .filter(c => String(c.serialNumber || c.serial) !== String(chargerSerial))
    .reduce((sum, c) => sum + (c.maxCurrent || c.ratedCurrent || 0), 0);

  const hasCapacity = currentLoad + chargerMaxCurrent <= maxBreakerCurrent;

  return {
    skipCheck: false,
    stationName,
    maxBreakerCurrent,
    currentLoad,
    chargerMaxCurrent,
    hasCapacity,
  };
}

// Command Handlers Hook
export const useChargerCommandHandlers = (summarySerialNumber, summarySiteId, sendCommand) => {
  const queryClient = useQueryClient();
  const [startChargingValidation, setStartChargingValidation] = useState({});
  const validateAbortRef = useRef(null);

  const handleReset = async resetType => {
    const payload = {
      sn: summarySerialNumber,
      site: summarySiteId,
      featureId: OCPP_1_6_MESSAGES.Reset, // 1017
      payload: {
        type: resetType,
      },
    };
    if (typeof sendCommand === "function") {
      await sendCommand(payload);
    }
  };

  const handleChangeConfig = configData => {
    const payload = {
      sn: summarySerialNumber,
      site: summarySiteId,
      featureId: OCPP_1_6_MESSAGES.ChangeConfiguration, // 1014
      payload: {
        key: configData.key,
        value: configData.value,
      },
    };
    try {
      if (typeof sendCommand === "function") {
        return sendCommand(payload);
      }
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const handleGetConfig = keys => {
    const payload = {
      sn: summarySerialNumber,
      site: summarySiteId,
      featureId: OCPP_1_6_MESSAGES.GetConfiguration, // 1015
      payload: {
        key: keys,
      },
    };
    try {
      if (typeof sendCommand === "function") {
        return sendCommand(payload);
      }
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const handleSendLocalList = items => {
    const payload = {
      sn: summarySerialNumber,
      site: summarySiteId,
      featureId: OCPP_1_6_MESSAGES.SendLocalList, // 1016
      payload: items,
    };
    try {
      if (typeof sendCommand === "function") {
        return sendCommand(payload);
      }
      return Promise.resolve();
    } catch (e) {
      return Promise.reject(e);
    }
  };

  const validateStartCharging = useCallback(async () => {
    if (validateAbortRef.current) validateAbortRef.current.abort();
    const controller = new AbortController();
    validateAbortRef.current = controller;
    setStartChargingValidation({ loading: true });
    try {
      const result = await validateBreakerCapacity(queryClient, summarySiteId, summarySerialNumber);
      if (controller.signal.aborted) return;
      setStartChargingValidation({ ...result, loading: false });
    } catch (e) {
      if (controller.signal.aborted) return;
      setStartChargingValidation({
        loading: false,
        error: "Failed to validate breaker capacity. Please try again.",
      });
    }
  }, [queryClient, summarySiteId, summarySerialNumber]);

  const handleStartCharging = useCallback(
    async chargingLevel => {
      return startCharging(summarySiteId, summarySerialNumber, chargingLevel);
    },
    [summarySiteId, summarySerialNumber]
  );

  const handleStopCharging = useCallback(async () => {
    return stopCharging(summarySiteId, summarySerialNumber);
  }, [summarySiteId, summarySerialNumber]);

  const resetStartChargingValidation = useCallback(() => {
    if (validateAbortRef.current) validateAbortRef.current.abort();
    setStartChargingValidation({});
  }, []);

  return {
    handleReset,
    handleChangeConfig,
    handleGetConfig,
    handleSendLocalList,
    validateStartCharging,
    handleStartCharging,
    handleStopCharging,
    startChargingValidation,
    resetStartChargingValidation,
  };
};
